import { HttpClient } from '@angular/common/http';
import { Type } from '@angular/core';
import { ISettings } from '@lsq-frontend/angular-shared/settings';
import { merge, Observable, of, Subject, Subscription } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    mapTo,
    shareReplay,
    switchMap
} from 'rxjs/operators';
import { pascalCaseRpcInUrl } from './helpers/http.helpers';
import { ErrorResponse } from './models/error';
import {
    Mk2Request,
    Mk2Response,
    ProtoRequest,
    ProtoResponse
} from './models/request-response-all-java';

export abstract class ApiService<
    TRequest extends Request | void | ProtoRequest | Mk2Request,
    TResponse extends Response | ProtoResponse | Mk2Response
> {
    private requestSubscription: Subscription;
    private _requestStream: Observable<TRequest>;
    private _preventExecutionViaRequestStream: boolean;
    private _executeSubject = new Subject<TRequest | void>();
    protected dataSubject = new Subject<TResponse>();
    protected errorSubject = new Subject<ErrorResponse>();

    public abstract requestType: Type<TRequest>;
    public abstract responseType: Type<TResponse>;
    public abstract endpointName: string;
    public serviceName: string;
    public usePascalForUrl = true;
    public dataStream = this.dataSubject.asObservable();
    public errorStream = this.errorSubject.asObservable();
    public executeStream = this._executeSubject.asObservable();
    public isInFlightStream: Observable<boolean>;

    public constructor(protected _http: HttpClient, protected _settings: ISettings) {
        this.executeStream
            .pipe(
                map((request) => {
                    if (typeof this.requestMapper === 'function') {
                        return this.requestMapper(request as TRequest);
                    }
                    return request;
                }),
                switchMap((request) =>
                    this._http.post(this.endpointUrl, request || {}).pipe(
                        map((response) => {
                            if (typeof this.responseMapper === 'function') {
                                return this.responseMapper(response);
                            }
                            return response;
                        }),
                        map((data) => ({ data, error: undefined })),
                        // Errors are handled in the response interceptor as well.
                        catchError((error) => of({ error, data: undefined }))
                    )
                )
            )
            .subscribe(({ data, error }) => {
                if (data) {
                    this.dataSubject.next(new this.responseType(data));
                }
                if (error) {
                    this.errorSubject.next(error);
                }
            });

        this.isInFlightStream = merge(
            this.executeStream.pipe(mapTo(true)),
            merge(this.dataStream, this.errorStream).pipe(mapTo(false))
        ).pipe(shareReplay(1));
        this.isInFlightStream.subscribe();
    }

    public get endpointUrl(): string {
        const serviceUrl =
            this._settings.httpApiServiceNameUrlMap[
                this.serviceName || this._settings.httpApiDefaultServiceName
            ];
        if (this.serviceName && !serviceUrl) {
            throw new Error(
                `No URL was found matching "${this.serviceName}" in SETTINGS.httpApiServiceNameUrlMap.`
            );
        }

        if (!this.usePascalForUrl) {
            return `${serviceUrl}/api/${this.endpointName}`;
        }
        return pascalCaseRpcInUrl(`${serviceUrl}/api/${this.endpointName}`);
    }

    public execute(request?: TRequest): void {
        this._executeSubject.next(request);
    }

    public executeOnce(request: TRequest): Promise<TResponse> {
        let actualRequest = request as unknown;
        if (typeof this.requestMapper === 'function') {
            actualRequest = this.requestMapper(request);
        }
        return new Promise<TResponse>((resolve, reject) => {
            // tslint:disable-next-line: no-floating-promises
            this._http
                .post<TResponse>(this.endpointUrl, actualRequest || {})
                .pipe(
                    map((response) => {
                        if (typeof this.responseMapper === 'function') {
                            return this.responseMapper(response);
                        }
                        return response;
                    }),
                    map((response) => new this.responseType(response))
                )
                .toPromise()
                .then((response) => {
                    // We filter out undefined because a closed stream will send undefined if the promise has not resolved
                    if (response !== undefined) {
                        resolve(response);
                    } else {
                        reject(null);
                    }
                })
                .catch((response) => reject(response));
        });
    }

    public get requestStream(): Observable<TRequest> {
        return this._requestStream;
    }

    public get requestStreamFiltered(): Observable<TRequest> {
        return this._requestStream.pipe(filter(() => !this._preventExecutionViaRequestStream));
    }

    public preventExecutionViaRequestStream(): void {
        this._preventExecutionViaRequestStream = true;
    }

    public allowExecutionViaRequestStream(): void {
        this._preventExecutionViaRequestStream = false;
    }

    public setRequestStreamAndAutoExecute(requestStream: Observable<TRequest>): void {
        this._requestStream = requestStream.pipe(
            filter((x) => !!x),
            distinctUntilChanged()
        );
        this._subscribeToRequestStream();
    }

    public setRequestStream(requestStream: Observable<TRequest>): void {
        this._requestStream = requestStream.pipe(
            filter((x) => !!x),
            distinctUntilChanged()
        );
    }

    public unsubscribeFromRequestStream(): void {
        if (this.requestSubscription) {
            this.requestSubscription.unsubscribe();
        }
    }

    /**
     * Meant to be overridden in cases where mapping is needed (conditional or otherwise complex) in order
     * to avoid having to re-implement `execute` and `executeOnce`.
     */
    public requestMapper(req: TRequest): unknown {
        return req;
    }

    /**
     * Meant to be overridden in cases where mapping is needed (conditional or otherwise complex) in order
     * to avoid having to re-implement `execute` and `executeOnce`.
     */
    public responseMapper(res: unknown): TResponse {
        return res as TResponse;
    }

    private _subscribeToRequestStream(): void {
        if (this.requestSubscription) {
            this.requestSubscription.unsubscribe();
        }
        this.requestSubscription = this.requestStream
            .pipe(filter(() => !this._preventExecutionViaRequestStream))
            .subscribe((request) => {
                this.execute(request);
            });
    }
}
